import { openDB, IDBPDatabase } from 'idb';

const DB_NAME = 'mediaStore';
const STORE_NAME = 'media';
const DB_VERSION = 1;

let db: IDBPDatabase | null = null;

async function initDB() {
  if (!db) {
    db = await openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME);
        }
      },
      blocked() {
        console.warn('Une ancienne version de la base de données est bloquée');
      },
      blocking() {
        console.warn('Une nouvelle version de la base de données est en attente');
        if (db) {
          db.close();
          db = null;
        }
      },
      terminated() {
        console.error('La base de données a été terminée de manière inattendue');
        db = null;
      }
    });
  }
  return db;
}

export async function storeMedia(key: string, data: string): Promise<void> {
  try {
    const db = await initDB();
    
    // Vérifier si la connexion est toujours active
    if (!db || db.version === undefined) {
      throw new Error('La connexion à la base de données a été perdue');
    }

    // Vérifier si la transaction est possible
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);

    // Stocker les données
    await store.put(data, key);

    // Attendre que la transaction soit terminée
    await tx.done;

    // Vérifier que les données ont bien été enregistrées
    const verification = await db.get(STORE_NAME, key);
    if (!verification) {
      throw new Error('La vérification du stockage a échoué');
    }

    // Déclencher un événement de stockage pour notifier les autres composants
    window.dispatchEvent(new Event('storage'));
  } catch (error) {
    console.error('Erreur lors du stockage des données:', error);
    // Réinitialiser la connexion en cas d'erreur
    db = null;
    throw error;
  }
}

export async function getMedia(key: string): Promise<string | undefined> {
  try {
    const db = await initDB();
    
    if (!db || db.version === undefined) {
      throw new Error('La connexion à la base de données a été perdue');
    }

    // Utiliser une transaction en lecture seule
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    
    const data = await store.get(key);
    
    // Attendre que la transaction soit terminée
    await tx.done;
    
    return data;
  } catch (error) {
    console.error('Erreur lors de la récupération des données:', error);
    db = null;
    throw error;
  }
}

export async function removeMedia(key: string): Promise<void> {
  try {
    const db = await initDB();
    
    if (!db || db.version === undefined) {
      throw new Error('La connexion à la base de données a été perdue');
    }

    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    
    await store.delete(key);
    await tx.done;

    // Déclencher un événement de stockage pour notifier les autres composants
    window.dispatchEvent(new Event('storage'));
  } catch (error) {
    console.error('Erreur lors de la suppression des données:', error);
    db = null;
    throw error;
  }
}

// Fonction pour vérifier l'état de la base de données
export async function checkDatabaseHealth(): Promise<boolean> {
  try {
    const db = await initDB();
    return db !== null && db.version !== undefined;
  } catch (error) {
    console.error('Erreur lors de la vérification de la base de données:', error);
    return false;
  }
}